/**
 * @author TEAM CMS
 *
 * Utility functions to generate video source strings for different platforms.
 */

import type { VideoType } from '../../react/VideoPlayer/VideoPlayer.types.js';

/**
 * Generate a SolidTango source string for embedding a video.
 *
 * @param videoId - The unique identifier for the SolidTango video.
 * @param api - Whether to include API parameters (default: false).
 * @param autoPlay - Whether the video should autoplay (default: false).
 * @returns The formatted SolidTango embed URL.
 */
function generateSolidTangoSrcString(videoId: string, api: boolean = false, autoPlay: boolean = false): string {
  const solidTangoOrigin = process.env.NEXT_PUBLIC_SOLIDTANGO_ORIGIN ?? 'https://svspelsportcasino.solidtango.com';
  return `${solidTangoOrigin}/widgets/embed/${videoId}?autoFillScreen=0${api ? '&api=true' : ''}${autoPlay ? '&auto_play=true' : ''}`;
}

/**
 * Generate a YouTube embed URL.
 *
 * @param videoId - The unique identifier for the YouTube video.
 * @param autoPlay - Whether the video should autoplay (default: false).
 * @returns The formatted YouTube embed URL.
 */
function generateYouTubeSrcString(videoId: string, autoPlay: boolean = false): string {
  return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&enablejsapi=1&autoplay=${autoPlay ? '1' : '0'}`;
}

/**
 * Generate the source URL for embedding a video.
 *
 * @param type The type of video platform (e.g., 'solidtango', 'youtube').
 * @param videoId The unique identifier for the video.
 * @param api Whether to include API parameters (default: false).
 * @param autoPlay Whether the video should autoplay (default: false).
 * @returns The formatted video source URL or null if unsupported.
 */
export function generateSrcString(
  type: VideoType,
  videoId: string,
  api: boolean = false,
  autoPlay: boolean = false
): string | null {
  switch (type) {
    case 'solidtango':
      return generateSolidTangoSrcString(videoId, api, autoPlay);
    case 'youtube':
      return generateYouTubeSrcString(videoId, autoPlay);
    default:
      return null;
  }
}
